import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { Message, User } from '@progress/kendo-angular-conversational-ui';

import { MessagesService } from '../shared/services/messages.service';
import { FeedbackService } from '../shared/services/feedback.service';
import { SessionsService } from '../shared/services/sessions.service';

import { ToastrService } from 'ngx-toastr';
import { Question } from '../shared/models/question';
import { ConversationMessage } from '../shared/models/conversation-message';
import { FeedbackType } from '../shared/models/feedback';
import {
  thumbDownIcon,
  thumbDownOutlineIcon,
  thumbUpIcon,
  thumbUpOutlineIcon,
  windowMinimizeIcon,
  windowRestoreIcon,
  xIcon,
  searchIcon
} from '@progress/kendo-svg-icons';


@Component({
    selector: 'app-agents-conversations',
    templateUrl: './conversations.component.html',
    styleUrls: ['./conversations.component.scss'],
    providers: [DatePipe],
    standalone: false
})
export class ConversationsComponent implements OnInit {
  readonly thumbDownIcon = thumbDownIcon;
  readonly thumbDownOutlineIcon = thumbDownOutlineIcon;
  readonly thumbUpIcon = thumbUpIcon;
  readonly thumbUpOutlineIcon = thumbUpOutlineIcon;
  readonly minimizeIcon = windowMinimizeIcon;
  readonly closeIcon = xIcon;
  readonly maximizeIcon = windowRestoreIcon;
  readonly searchIcon = searchIcon;

  public gridView: GridDataResult;
  public gridState: State = {
    skip: 0,
    take: 20,
    sort: [{field: 'timestamp', dir: 'desc'}]
  };
  public expandedKeys = new Set<string>();

  public activeChatSession: ConversationMessage[] | null = null;
  public chatMessages: Message[] = [];
  public chatState: 'closed' | 'open' | 'reduced' = 'closed';
  public readonly user: User = {id: 'user'};
  public readonly agent: User = {id: 'agent', name: 'Agent'};

  public improvementText: { [messageId: string]: string } = {};
  public updatedBy: { [messageId: string]: string } = {};

  public search = "";

  constructor(
    private messagesService: MessagesService,
    private feedbackService: FeedbackService,
    private sessionsService: SessionsService,
    private toastrService: ToastrService,
  ) {
  }

  ngOnInit(): void {
    this.loadMessages();
  }

  public dataStateChange(state: DataStateChangeEvent): void {
    this.gridState = state;
    this.loadMessages();
  }

  public loadMessages(): void {
    const {skip, take, sort, filter} = this.gridState;
    const page = Math.floor(skip / take) + 1;
    const sortField = sort.length ? sort[0].field : 'timestamp';
    const sortOrder = sort.length ? sort[0].dir : 'desc';
    const search = filter && filter.filters.length ? JSON.stringify(filter) : ''; // Basic search, can be improved

    this.messagesService.getMessages(page, take, sortField, sortOrder, this.search).subscribe({
      next: (res) => {
        this.gridView = {
          data: res.items,
          total: res.total_items
        };
      },
      error: (err) => console.log(err)
    });
  }

  public expandRow(e: { dataItem: Question }): void {
    this.expandedKeys.add(e.dataItem.answer.messageId);
    // Pre-fill form fields when expanding
    if (e.dataItem.answer?.feedback) {
      this.improvementText[e.dataItem.answer.messageId] = e.dataItem.answer.feedback.improvedResponse || '';
      this.updatedBy[e.dataItem.answer.messageId] = e.dataItem.answer.feedback.updatedBy || '';
    }
  }

  public collapseRow(e: { dataItem: Question }): void {
    this.expandedKeys.delete(e.dataItem.answer.messageId);
  }

  public giveFeedback(dataItem: Question, feedbackType: FeedbackType): void {
    if (dataItem.answer) {
      this.feedbackService.postFeedback(dataItem.answer.messageId, {type: feedbackType, source: 'admin'})
        .subscribe(() => this.loadMessages()); // Reload data to show changes
    }
  }

  public saveImprovement(dataItem: Question): void {
    const messageId = dataItem.answer.messageId;
    const text = this.improvementText[messageId];
    const admin = this.updatedBy[messageId];

    if (!text || !admin) {
      this.toastrService.error('Please provide both an improved response and an admin username.');
      return;
    }

    if (dataItem.answer?.feedback?.createdAt) {
      // Patch if feedback already exists
      this.messagesService.updateImprovedResponse(messageId, {improvedResponse: text, updatedBy: admin})
        .subscribe(() => {
          this.toastrService.success('Improvement updated!');
          this.loadMessages();
        });
    } else {
      // Post if it's a new improvement
      this.messagesService.improveResponse(messageId, {improvedResponse: text, updatedBy: admin})
        .subscribe(() => {
          this.toastrService.success('Improvement saved!');
          this.loadMessages();
        });
    }
  }

  public deleteMessage(messageId: string): void {
    if (confirm('Are you sure you want to delete this message?')) {
      this.messagesService.deleteMessage(messageId)
        .subscribe(() => this.loadMessages());
    }
  }

  public showInChat(dataItem: Question): void {
    this.sessionsService.getConversation(dataItem.userSessionId).subscribe(messages => {
      this.activeChatSession = messages;
      this.chatMessages = this.activeChatSession.map(m => ({
        author: m.author === 'agent' ? this.agent : this.user,
        text: m.text,
        timestamp: new Date(m.timestamp)
      }));
      this.chatState = 'open';
    });
  }

  public reduceChat(): void {
    this.chatState = 'reduced';
  }

  public expandChat(): void {
    this.chatState = 'open';
  }

  public closeChat(): void {
    this.chatState = 'closed';
    this.activeChatSession = null;
    this.chatMessages = [];
  }
}
